import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { H6, P, UL, LI } from "../../../AbstractElements";
import { settingIconData } from "../../../Data/Layout/RightHeader";
import SvgIcon from "../../../Utils/CommonComponents/CommonSvgIcons";
import { baseUrl } from "../../../Utils/Baseurl/Baseurl";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/scss/main.scss";
import { jwtDecode } from "jwt-decode";

interface User {
  id: string;
  name: string;
  usertype: number;
  disablestatus?: number;
}

interface CustomJwtPayload {
  id: string;
  email: string;
}

export default function UserWrap() {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setLoading(false);
      } catch (error) {
        console.error("Error parsing user data:", error);
        localStorage.removeItem("user");
      }
    } else {
      fetchUserProfile();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) throw new Error("No authentication token found");

      const response = await axios.get(`${baseUrl}login/profile`, {
        headers: { Authorization: token },
      });

      if (response.data) {
        setUser({
          ...response.data,
          usertype: response.data.user?.usertype,
        });

        localStorage.setItem(
          "user",
          JSON.stringify({
            ...response.data,
            usertype: response.data.user?.usertype,
          })
        );
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => setOpen(!open);

  const confirmLogout = () => {
    toast.warn(
      <div>
        <p>Are you sure you want to log out?</p>
        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <button
            onClick={() => {
              logout();
              toast.dismiss();
            }}
            style={{
              backgroundColor: "red",
              color: "white",
              border: "none",
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            Yes
          </button>
          <button
            onClick={() => toast.dismiss()}
            style={{
              backgroundColor: "gray",
              color: "white",
              border: "none",
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            No
          </button>
        </div>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const logout = () => {
    localStorage.clear();
    toast.info("Logged out successfully!");
    navigate("/login", { replace: true });
  };

  const getUserType = () => {
    if (user?.usertype == null) {
      return "Loading...";
    }

    switch (Number(user?.usertype)) {
      case 1:
        return "Medical Representative";
      case 2:
        return "Purchase Team";
      case 3:
        return "Purchase Manager";
      default:
        return "Unknown Role";
    }
  };

  const confirmDisableUser = () => {
    toast.warn(
      ({ closeToast }) => (
        <div>
          <p>Are you sure you want to disable this account?</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
            <button
              onClick={() => {
                disableUser();
                closeToast();
              }}
              style={{
                backgroundColor: "red",
                color: "white",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
              }}
            >
              Yes
            </button>
            <button
              onClick={closeToast}
              style={{
                backgroundColor: "gray",
                color: "white",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
              }}
            >
              No
            </button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false }
    );
  };

  const disableUser = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("User is not logged in!");
      return;
    }

    try {
      const decoded = jwtDecode<CustomJwtPayload>(token);
      const userId = decoded.id;
      const email = decoded.email;

      if (!userId || !email) {
        toast.error("Invalid token, user ID or email not found!");
        return;
      }

      await axios.put(
        `${baseUrl}user/disable`,
        { userId, disablestatus: 1, email },
        { headers: { Authorization: token } }
      );

      toast.dismiss();
      logout();
    } catch (error) {
      console.error("Error disabling user:", error);
      toast.error("Failed to disable user!");
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <li className="profile-nav custom-dropdown" ref={dropdownRef}>
        <div className="user-wrap" onClick={toggle}>
          <div className="user-img"></div>
          <div className="user-content">
            <H6>{user?.name || "Unknown User"}</H6>
            <P className="mb-0">
              {getUserType()} <i className="fa-solid fa-chevron-down" />
            </P>
          </div>
        </div>
        <div className={`custom-menu overflow-hidden ${open ? "show" : ""}`}>
          <UL className="profile-body simple-list">
            {settingIconData.map((item) => (
              <LI className="d-flex" key={item.id} onClick={() => setOpen(false)}>
                <SvgIcon className="svg-color" iconId={item.icon} />
                <Link className="ms-2" to={`${process.env.PUBLIC_URL}/${item.link}`}>
                  {item.text}
                </Link>
              </LI>
            ))}
            <LI className="d-flex" onClick={() => { confirmDisableUser(); setOpen(false); }}>
              <SvgIcon className="svg-color" iconId="minus" />
              <Link className="ms-2" to="#">
                Disable
              </Link>
            </LI>
            <LI className="d-flex" onClick={() => { confirmLogout(); setOpen(false); }}>
              <SvgIcon className="svg-color" iconId="Login" />
              <Link className="ms-2" to="#">
                Log Out
              </Link>
            </LI>
          </UL>
        </div>
      </li>
    </>
  );
}
